import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {
  Box,
  Button,
  CardActions,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttractionsIcon from "@mui/icons-material/Attractions";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import StatusChip from "../chips/status.chip";
import { useAuth } from "../../context/auth.context";
import { useSnackbar } from "../../context/snackbar.context";
import { joinGather, leaveGather } from "../../api/gather.api";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../api/user.api";
import UserCard from "./user.card";

const GatherCard = ({ gather }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const [owner, setOwner] = useState();

  useEffect(() => {
    getUser(gather.ownerId).then((response) => {
      setOwner(response.data);
    });
  }, [gather]);

  const isJoinable = () => {
    return (
      gather.status === "Open" &&
      !gather.participantIds.find((id) => id === auth.user.id) &&
      gather.ownerId !== auth.user.id
    );
  };

  const isLeavable = () => {
    return (
      gather.status === "Open" &&
      gather.participantIds.find((id) => id === auth.user.id) &&
      gather.ownerId !== auth.user.id
    );
  };

  const handleJoin = () => {
    joinGather(gather)
      .then((result) => {
        if (!result.data) {
          showSnackbar("Could not join!", "error");
        } else {
          showSnackbar("Joined successfully!", "success");
        }
      })
      .catch((error) => {
        showSnackbar("Could not join! Unknown Error", "error");
      });
  };

  const handleLeave = () => {
    leaveGather(gather)
      .then((response) => {
        if (response.data) {
          showSnackbar("Leaved successfully!", "success");
        } else {
          showSnackbar("Leaving failed!", "error");
        }
      })
      .catch(() => showSnackbar("Leaving failed!", "error"));
  };

  const handleNavigate = () => {
    navigate("/gather", { state: { gather } });
  };

  return (
    <Card
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
         minWidth: 300,
        maxWidth: 300,
        minHeight: 350,
        maxHeight: 350,
        overflow: "hidden", // wichtig für das ::before
        "&::before": gather.game.image
          ? {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(
              rgba(0,0,0,0.5), 
              rgba(18,18,18,1)
            ), url(/static/images/${gather.game.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.4,
              transition: "opacity 0.4s ease",
              zIndex: 0,
            }
          : {},
        "&:hover::before": {
          transition: "opacity 0.4s ease",
          opacity: 0, // Hover-Effekt
        },
        "& > *": {
          position: "relative",
          zIndex: 1, // sorgt dafür, dass der Inhalt über dem Hintergrundbild bleibt
        },
      }}
    >
      <CardContent>
        <Stack spacing={1} direction="row" sx={{ alignItems: "center", mb: 1, justifyContent:"center" }}>
          <PersonSearchIcon />
          <Typography variant="h6" color="primary" sx={{ mr: 1 }} gutterBottom>
            {gather.name}
          </Typography>
        </Stack>

        <Divider variant="middle" sx={{ mt: 1, mb: 1 }}/>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{display: "flex", justifyContent:"center", alignItems:"center"}}>
            <Typography sx={{ color: "text.secondary", mr: 3 }}>
              {gather.game.name}
            </Typography>
            {gather.game.category === "Shooter" && (
              <CrisisAlertIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            {gather.game.category === "Racing" && (
              <DirectionsCarIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            {gather.game.category === "Strategy" && (
              <AccountBalanceIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            {gather.game.category === "Other" && (
              <AttractionsIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {gather.game.category}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <StatusChip status={gather.status} />
          </Stack>
        </Box>
        <Divider variant="middle" sx={{ mt: 1, mb: 1 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography>Owner</Typography>
          {owner && <UserCard user={owner} />}
        </Box>
        <Divider variant="middle" sx={{ mt: 1, mb: 1 }} />
        <Typography
          variant="body"
          sx={{
            color: "text.secondary",
            fontSize: 10,
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "250px",
            maxHeight: "100px",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            display: "-webkit-box",
          }}
        >
          {gather.description}
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
          Participants: {gather.participantIds.length} /{" "}
          {gather.maxParticipants}
        </Typography>
      </CardContent>
      <CardActions>
        {isJoinable() && (
          <Button size="small" onClick={handleJoin}>
            Join
          </Button>
        )}
        {isLeavable() && (
          <Button size="small" onClick={handleLeave}>
            Leave
          </Button>
        )}
        <Button size="small" onClick={handleNavigate}>
          Details
        </Button>
      </CardActions>
    </Card>
  );
};

export default GatherCard;
